import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import P11AdminLayout from "./P11AdminLayout";
import { useP11Role } from "./P11AdminGuard";
import { P11, fmtDate } from "./p11shared";

// Snelle aanwezigheid: zoeken, present/absent/not recorded, bulk-verify, revoke met reden.
export default function P11AdminAttendance() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const role = useP11Role();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [confirmVerify, setConfirmVerify] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const canWrite = role === "owner" || role === "coach";

  const load = () => api.get(`/project11/admin/sessions/${sessionId}/roster`).then(({ data }) => setData(data)).catch(() => setData(false));
  useEffect(() => { load(); }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.rows;
    const n = q.toLowerCase();
    return data.rows.filter((r) => (r.name + " " + (r.number_display || "")).toLowerCase().includes(n));
  }, [data, q]);

  const mark = async (r, status) => {
    try {
      await api.post(`/project11/admin/sessions/${sessionId}/attendance`, { participant_id: r.participant_id, status });
      load();
    } catch (err) {
      if (err?.response?.data?.detail === "REVOKE_REQUIRED") toast.error("Eerst stempel intrekken (met reden)");
      else toast.error("Registreren mislukt");
    }
  };

  const bulkVerify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/project11/admin/sessions/${sessionId}/verify`);
      toast.success(`${data.count} stempel(s) geverifieerd`);
      setConfirmVerify(false);
      load();
    } catch { toast.error("Verifiëren mislukt"); } finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.post(`/project11/admin/sessions/${sessionId}/revoke`, { participant_id: revokeTarget.participant_id, reason });
      toast.success("Stempel ingetrokken");
      setRevokeTarget(null); setReason("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Intrekken mislukt"); } finally { setBusy(false); }
  };

  if (data === null) return <P11AdminLayout><span className="p11-label">…</span></P11AdminLayout>;
  if (data === false) return <P11AdminLayout><span className="p11-label" data-testid="p11-roster-missing">Sessie niet gevonden.</span></P11AdminLayout>;
  const { session, progress } = data;
  const pendingCount = data.rows.filter((r) => r.attendance === "present" && !r.verified).length;

  return (
    <P11AdminLayout>
      <div data-testid="p11-admin-attendance">
        <button onClick={() => nav("/project11/admin/sessions")} className="p11-label hover:text-white mb-6" data-testid="p11-roster-back">← SESSIONS</button>
        <div className="p11-label-yellow">PROJECT 11* / SESSIE {String(session.number).padStart(2, "0")}</div>
        <h1 className="p11-h1 text-3xl mt-3">{fmtDate(session.date).toUpperCase()}</h1>
        <div className="p11-label mt-2">{session.start_time}–{session.end_time} · {session.location}</div>

        <div className="flex flex-wrap items-center gap-6 mt-8 mb-6">
          <div data-testid="p11-roster-progress">
            <span className="p11-mono text-2xl" style={{ color: P11.yellow }}>{progress.reviewed}</span>
            <span className="p11-mono text-2xl" style={{ color: P11.gray }}>/{progress.expected}</span>
            <div className="p11-label mt-1">BEOORDEELD · {progress.unreviewed} OPEN</div>
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek naam of nummer…" className="p11-input flex-1 min-w-48" data-testid="p11-roster-search" />
          {canWrite && pendingCount > 0 && (
            <button onClick={() => setConfirmVerify(true)} className="p11-btn" data-testid="p11-roster-bulk-verify">VERIFY PRESENT PARTICIPANTS → ({pendingCount})</button>
          )}
        </div>

        {rows.length === 0 ? (
          <div className="border p-10 text-center" style={{ borderColor: P11.border }} data-testid="p11-roster-empty">
            <div className="p11-h2 text-lg">Geen deelnemers op deze sessie.</div>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: P11.border }} data-testid="p11-roster-list">
            {rows.map((r) => (
              <div key={r.participant_id} className={`py-4 flex flex-wrap items-center gap-3 px-2 ${r.attendance === "unrecorded" ? "bg-[#171717]/60" : ""}`} data-testid={`p11-roster-row-${r.participant_id}`}>
                <span className="p11-mono w-14" style={{ color: r.number_display ? P11.yellow : P11.gray }}>{r.number_display || "———"}</span>
                <span className="flex-1 min-w-36 text-sm" style={{ color: P11.text }}>{r.name}</span>
                <span className="p11-label" style={{ color: r.verified ? P11.yellow : r.attendance === "present" ? P11.gray2 : P11.gray }} data-testid={`p11-roster-status-${r.participant_id}`}>
                  {r.verified ? "VERIFIED" : r.attendance === "present" ? "PENDING" : r.attendance.toUpperCase().replace("_", " ")}
                </span>
                {canWrite && (
                  <div className="flex gap-2">
                    <button onClick={() => mark(r, "present")} className="p11-label px-3 py-1.5 border transition-colors" style={{ borderColor: r.attendance === "present" ? P11.yellow : P11.border, color: r.attendance === "present" ? P11.yellow : P11.gray2 }} data-testid={`p11-roster-present-${r.participant_id}`}>PRESENT</button>
                    <button onClick={() => mark(r, "absent")} className="p11-label px-3 py-1.5 border transition-colors" style={{ borderColor: r.attendance === "absent" ? "#e08585" : P11.border, color: r.attendance === "absent" ? "#e08585" : P11.gray2 }} data-testid={`p11-roster-absent-${r.participant_id}`}>ABSENT</button>
                    <button onClick={() => mark(r, "unrecorded")} className="p11-label px-3 py-1.5 border transition-colors" style={{ borderColor: P11.border, color: P11.gray }} data-testid={`p11-roster-clear-${r.participant_id}`}>NOT RECORDED</button>
                    {r.verified && <button onClick={() => setRevokeTarget(r)} className="p11-label px-3 py-1.5 border" style={{ borderColor: "#7a2d2d", color: "#e08585" }} data-testid={`p11-roster-revoke-${r.participant_id}`}>REVOKE</button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {confirmVerify && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setConfirmVerify(false)}>
            <div className="bg-[#080808] border max-w-sm w-full p-8" style={{ borderColor: P11.border }} onClick={(e) => e.stopPropagation()} data-testid="p11-verify-confirm">
              <div className="p11-label-yellow mb-4">// BULK VERIFICATIE</div>
              <p className="text-sm" style={{ color: P11.gray2 }}>{pendingCount} als PRESENT gemarkeerde deelnemer(s) worden geverifieerd. Stempels worden toegekend en vastgelegd in de auditlog.</p>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setConfirmVerify(false)} className="p11-btn-ghost" data-testid="p11-verify-no">ANNULEREN</button>
                <button onClick={bulkVerify} disabled={busy} className="p11-btn" data-testid="p11-verify-yes">{busy ? "…" : "VERIFIËREN"}</button>
              </div>
            </div>
          </div>
        )}

        {revokeTarget && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setRevokeTarget(null)}>
            <div className="bg-[#080808] border max-w-sm w-full p-8" style={{ borderColor: P11.border }} onClick={(e) => e.stopPropagation()} data-testid="p11-revoke-modal">
              <div className="p11-label-yellow mb-4">// STEMPEL INTREKKEN</div>
              <p className="text-sm mb-4" style={{ color: P11.gray2 }}>Stempel van {revokeTarget.name} intrekken? Een reden is verplicht en wordt vastgelegd.</p>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reden…" className="p11-input" data-testid="p11-revoke-reason" />
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setRevokeTarget(null)} className="p11-btn-ghost" data-testid="p11-revoke-no">ANNULEREN</button>
                <button onClick={revoke} disabled={busy || !reason.trim()} className="p11-btn" data-testid="p11-revoke-yes">{busy ? "…" : "INTREKKEN"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </P11AdminLayout>
  );
}
